import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { listarPessoas, type FiltroPessoa } from '../../../src/services/indicacoes';
import { ROTULO_RESULTADO, SELO_CLIENTE, formatarDataHora, primeiroNome } from '../../../src/lib/indicacoes/rotulos';
import { formatarTelefoneBr } from '../../../src/lib/enderecoUc';
import type { IndicacaoPessoaResumo } from '../../../types';
import FichaPessoaDrawer, { type AlvoFicha } from './FichaPessoaDrawer';
import RegistrarAbordagemModal from './RegistrarAbordagemModal';
import Selo from './Selo';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO, CLASSE_CARTAO, CLASSE_INPUT } from './estilos';

const POR_PAGINA = 50;

const FILTROS: { id: FiltroPessoa; rotulo: string }[] = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'nunca_abordados', rotulo: 'Nunca abordados' },
  { id: 'voltar_depois', rotulo: 'Pediu para voltar' },
  { id: 'abordados_ha_mais_de', rotulo: 'Abordados há mais de…' },
  { id: 'clientes', rotulo: 'Clientes' },
  { id: 'nao_clientes', rotulo: 'Não clientes' },
];

interface Props {
  versao: number;
  onMudou: () => void;
  gere: boolean;
}

const PessoasTab: React.FC<Props> = ({ versao, onMudou, gere }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtro, setFiltro] = useState<FiltroPessoa>('todos');
  const [dias, setDias] = useState(90);
  const [pagina, setPagina] = useState(0);
  const [linhas, setLinhas] = useState<IndicacaoPessoaResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ficha, setFicha] = useState<AlvoFicha | null>(null);
  const [novaAbordagem, setNovaAbordagem] = useState(false);

  // Link "Ver ficha" vindo da negociação: /crm/indicacoes?pessoa=<id>
  useEffect(() => {
    const id = searchParams.get('pessoa');
    if (!id) return;
    setFicha({ pessoaId: id, cpfCnpj: null });
    searchParams.delete('pessoa');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(0);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setLinhas(await listarPessoas({ busca: buscaAplicada, filtro, dias, limite: POR_PAGINA, offset: pagina * POR_PAGINA }));
    } catch (e) {
      setErro((e as Error).message);
      setLinhas([]);
    } finally {
      setCarregando(false);
    }
  }, [buscaAplicada, filtro, dias, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  const total = linhas[0]?.total ?? 0;
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  return (
    <div className="space-y-3">
      <div className={`${CLASSE_CARTAO} p-3 flex flex-col lg:flex-row gap-2 lg:items-center`}>
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className={`${CLASSE_INPUT} pl-9`}
            placeholder={gere ? 'Buscar por nome, CPF/CNPJ, WhatsApp ou UC' : 'Buscar nas pessoas que você abordou'}
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        <select
          className={`${CLASSE_INPUT} lg:w-56`}
          value={filtro}
          onChange={e => {
            setFiltro(e.target.value as FiltroPessoa);
            setPagina(0);
          }}
        >
          {FILTROS.map(f => (
            <option key={f.id} value={f.id}>{f.rotulo}</option>
          ))}
        </select>
        {filtro === 'abordados_ha_mais_de' && (
          <select
            className={`${CLASSE_INPUT} lg:w-32`}
            value={dias}
            onChange={e => {
              setDias(Number(e.target.value));
              setPagina(0);
            }}
          >
            {[30, 60, 90, 180].map(d => (
              <option key={d} value={d}>{d} dias</option>
            ))}
          </select>
        )}
        <button onClick={() => setNovaAbordagem(true)} className={CLASSE_BOTAO_PRIMARIO}>
          <Plus size={15} /> Registrar abordagem
        </button>
      </div>

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      <div className={`${CLASSE_CARTAO} overflow-x-auto`}>
        {carregando ? (
          <LoadingSpinner label="Carregando pessoas..." />
        ) : linhas.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-12">
            {gere ? 'Ninguém encontrado com esse filtro.' : 'Você ainda não registrou nenhuma abordagem.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-700">
              <tr>
                <th className="text-left font-semibold px-4 py-2">Pessoa</th>
                <th className="text-left font-semibold px-4 py-2">UCs / cidade</th>
                <th className="text-left font-semibold px-4 py-2">Última abordagem</th>
                <th className="text-left font-semibold px-4 py-2">Indicações</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
              {linhas.map(l => {
                const selo = l.cliente ? SELO_CLIENTE.sim : SELO_CLIENTE.nao;
                return (
                  <tr key={l.pessoa_id ?? l.cpf_cnpj ?? l.nome} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-gray-900 dark:text-white">{l.nome}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Selo classe={selo.classe}>{selo.rotulo}</Selo>
                        {l.whatsapp && <span className="text-xs text-gray-500 dark:text-slate-400">{formatarTelefoneBr(l.whatsapp)}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-slate-300">
                      {l.ucs > 0 ? `${l.ucs} UC${l.ucs > 1 ? 's' : ''}` : '—'}
                      {l.cidade ? ` · ${l.cidade}` : ''}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {l.ultima_abordagem_em ? (
                        <>
                          <p className="text-gray-700 dark:text-slate-200">
                            {formatarDataHora(l.ultima_abordagem_em)}
                            {l.ultima_abordagem_por ? ` · ${primeiroNome(l.ultima_abordagem_por)}` : ''}
                          </p>
                          {l.ultimo_resultado && <p className="text-gray-500 dark:text-slate-400">{ROTULO_RESULTADO[l.ultimo_resultado]}</p>}
                        </>
                      ) : (
                        <span className="text-gray-400 dark:text-slate-500">Nunca abordada</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-slate-200">
                      {l.indicacoes} · {l.aprovadas} aprovada{l.aprovadas === 1 ? '' : 's'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setFicha({ pessoaId: l.pessoa_id, cpfCnpj: l.cpf_cnpj })}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > POR_PAGINA && (
        <div className="flex items-center justify-end gap-2 text-xs text-gray-600 dark:text-slate-300">
          <span>Página {pagina + 1} de {ultimaPagina + 1} · {total} pessoas</span>
          <button disabled={pagina === 0} onClick={() => setPagina(p => p - 1)} className={CLASSE_BOTAO_SECUNDARIO}>
            <ChevronLeft size={14} />
          </button>
          <button disabled={pagina >= ultimaPagina} onClick={() => setPagina(p => p + 1)} className={CLASSE_BOTAO_SECUNDARIO}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {ficha && <FichaPessoaDrawer alvo={ficha} gere={gere} onClose={() => setFicha(null)} onMudou={onMudou} />}
      {novaAbordagem && <RegistrarAbordagemModal gere={gere} onClose={() => setNovaAbordagem(false)} onSalvo={onMudou} />}
    </div>
  );
};

export default PessoasTab;
